import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, IconButton, CircularProgress, Alert, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Avatar, Chip, TablePagination, Menu, MenuItem, ListItemIcon as MuiListItemIcon,
    TextField, InputAdornment, Select, InputLabel, FormControl, LinearProgress
} from '@mui/material';
import { Add, Refresh, Edit, Delete, Visibility, MoreVert, Search, AccountBalance, RestaurantMenu } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';
import { getAllFoodVendors, deleteFoodVendor } from '../../services/Api';

const GetAllFoodVendors = () => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');

    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedVendor, setMenuSelectedVendor] = useState(null);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    useEffect(() => {
        setPage(0);
    }, [isActiveFilter]);

    const fetchVendors = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllFoodVendors(page + 1, rowsPerPage, debouncedSearch, isActiveFilter);
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.vendors)) targetArray = src.vendors;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                const total = response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || 0;
                setVendors(targetArray);
                setTotalRows(total);
            } else {
                setError(response.message || 'Failed to fetch food vendors');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching food vendors');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setIsActiveFilter('');
        setPage(0);

        setLoading(true);
        setError(null);
        try {
            const response = await getAllFoodVendors(1, rowsPerPage, '', '');
            if (response.success || response.data) {
                const src = response.data || response;
                let targetArray = [];

                if (Array.isArray(src)) targetArray = src;
                else if (src && typeof src === 'object') {
                    if (Array.isArray(src.vendors)) targetArray = src.vendors;
                    else if (Array.isArray(src.data)) targetArray = src.data;
                }

                const total = src?.totalRecords || src?.totalItems || src?.totalCount || src?.totalDocs || src?.total || src?.totalElements || targetArray.length || 0;
                setVendors(targetArray);
                setTotalRows(total);
            } else {
                setError(response.message || 'Failed to fetch food vendors');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching food vendors');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, isActiveFilter]);

    const handleMenuOpen = (event, vendor) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedVendor(vendor);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedVendor(null);
    };

    const handleDeleteClick = () => {
        setSelectedVendor(menuSelectedVendor);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await deleteFoodVendor(selectedVendor.id);
            if (response.success || response.message) {
                setDeleteDialogOpen(false);
                fetchVendors();
            } else {
                console.error('Failed to delete food vendor');
            }
        } catch (err) {
            console.error('An error occurred while deleting the food vendor:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        Food Vendors
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your food vendors efficiently
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton onClick={handleRefresh} disabled={loading} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}>
                        <Refresh />
                    </IconButton>
                    <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/vendors/food/create')} sx={{ px: 4, backgroundColor: 'primary.main' }}>
                        Create Vendor
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={isActiveFilter} label="Status" onChange={(e) => setIsActiveFilter(e.target.value)}>
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    placeholder="Search vendor..." size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: 300, '& .MuiOutlinedInput-root': { borderRadius: '20px', backgroundColor: 'rgba(0,0,0,0.02)' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment> }}
                />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
                {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, zIndex: 2 }} />}
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="food vendors table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Shop Name</TableCell>
                                
                                <TableCell sx={{ fontWeight: 700 }}>Owner Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Mobile</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Auto Accept</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s ease-in-out' }}>
                            {loading && vendors.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 10 }}><CircularProgress /></TableCell></TableRow>
                            ) : vendors.length === 0 ? (
                                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                                    <Typography color="text.secondary">No food vendors found. <Button onClick={() => navigate('/vendors/food/create')}>Create your first vendor</Button></Typography>
                                </TableCell></TableRow>
                            ) : (
                                vendors.map((vendor) => (
                                    <TableRow key={vendor.id} hover>
                                        <TableCell><Avatar src={vendor.image} alt={vendor.shopName} /></TableCell>
                                        <TableCell>{vendor.shopName}</TableCell>
                                       
                                        <TableCell>{vendor.ownerName}</TableCell>
                                        <TableCell>{vendor.mobile}</TableCell>
                                        <TableCell>{vendor.email}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                size="small" 
                                                label={vendor.auto_accept_orders ? "ON" : "OFF"} 
                                                variant="outlined"
                                                color={vendor.auto_accept_orders ? "success" : "default"} 
                                            />
                                        </TableCell>
                                         <TableCell>{vendor.locationName || vendor.location || 'N/A'}</TableCell>
                                        <TableCell><Chip size="small" label={vendor.isActive ? "Active" : "Inactive"} color={vendor.isActive ? "success" : "default"} /></TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, vendor)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]} component="div" count={totalRows} rowsPerPage={rowsPerPage} page={page}
                    onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} elevation={1}
                PaperProps={{ sx: { minWidth: 150, border: '1px solid', borderColor: 'text.border', mt: 1 } }}
            >
                <MenuItem onClick={() => { navigate(`/vendors/food/details/${menuSelectedVendor?.id}`); handleMenuClose(); }}>
                    <MuiListItemIcon><Visibility fontSize="small" color="primary" /></MuiListItemIcon>View Details
                </MenuItem>
                <MenuItem onClick={() => { navigate(`/vendors/food/modify/${menuSelectedVendor?.id}`); handleMenuClose(); }}>
                    <MuiListItemIcon><Edit fontSize="small" color="info" /></MuiListItemIcon>Edit Vendor
                </MenuItem>
                <MenuItem onClick={() => { navigate(`/vendors/food/bank-details/${menuSelectedVendor?.id}`); handleMenuClose(); }}>
                    <MuiListItemIcon><AccountBalance fontSize="small" color="success" /></MuiListItemIcon>Bank Details
                </MenuItem>
                <MenuItem onClick={() => { navigate(`/vendors/food/menu-items/${menuSelectedVendor?.id}`); handleMenuClose(); }}>
                    <MuiListItemIcon><RestaurantMenu fontSize="small" color="warning" /></MuiListItemIcon>View Menu Items
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>Delete
                </MenuItem>
            </Menu>

            {deleteDialogOpen && (
                <DeleteConfirm
                    open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} onConfirm={handleConfirmDelete} title="Delete Food Vendor"
                    description={`Are you sure you want to delete ${selectedVendor?.shopName}? This action cannot be undone.`} loading={loading}
                />
            )}
        </Box>
    );
};

export default GetAllFoodVendors;
